"use client";

import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { DietInfoDialog } from "@/features/diets/components/diet-info-dialog/diet-info-dialog";
import type { DietCatalog, DietCatalogDetail } from "@/features/diets/types";

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="space-y-3">
    <h3 className="font-medium text-base">{title}</h3>
    {children}
  </section>
);

/**
 * "More about the diet and the catering" — the full diet description together
 * with the catering's own description, opened from the diet card header.
 */
export const DietCateringInfoDialog = ({
  diet,
  trigger,
}: {
  diet: DietCatalog | DietCatalogDetail;
  trigger: ReactNode;
}) => {
  const t = useTranslations("diets.info");
  const tTags = useTranslations("dietTags");

  return (
    <DietInfoDialog title={t("title")} trigger={trigger}>
      <div className="space-y-6">
        <Section title={t("aboutDiet")}>
          {diet.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {diet.tags.map((tag) => (
                <Badge
                  className="rounded-full border-0 bg-brand-brown/10 px-3 py-1.5 font-medium text-brand-brown text-xs"
                  key={tag.slug}
                  variant="outline"
                >
                  {tTags(tag.slug)}
                </Badge>
              ))}
            </div>
          )}
          <div
            className="text-sm leading-[22px]"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: catalog descriptions are authored HTML, rendered the same way across the diet listing
            dangerouslySetInnerHTML={{ __html: diet.description }}
          />
        </Section>
        {diet.catering.description && (
          <>
            <hr className="border-brand-grey2-300" />
            <Section title={t("aboutCatering")}>
              <div
                className="text-sm leading-[22px]"
                // biome-ignore lint/security/noDangerouslySetInnerHtml: catalog descriptions are authored HTML, rendered the same way across the diet listing
                dangerouslySetInnerHTML={{ __html: diet.catering.description }}
              />
            </Section>
          </>
        )}
      </div>
    </DietInfoDialog>
  );
};
